import {createAbortControl} from '@raison/toolbox';
import {useEqualsMemo, useNonReactiveCallback} from '@raison/ui-kit';
import {absurd, identity, pipe} from 'effect/Function';
import {multiply} from 'effect/Number';
import * as Schema from 'effect/Schema';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isMobileApp} from '../utils/mobileDeviceDetect';

interface Props {
    isRefreshing: boolean;
    onRefresh: () => void;
    spinner: React.ReactElement<{
        spinning: boolean;
    }>;
    children?: React.ReactNode;
}

export function PullToRefreshContainer(props: Props) {
    if (isMobileApp() || import.meta.env.DEV) {
        return PullToRefreshContainerImpl(props);
    } else {
        return props.children;
    }
}

function PullToRefreshContainerImpl({isRefreshing, onRefresh: refreshHandler, spinner, children}: Props) {
    const doFeedback = useCallback(() => {
        if (navigator.vibrate) {
            navigator.vibrate(10);
        }
    }, []);

    const scrollerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const spinnerRef = useRef<HTMLDivElement>(null);

    const [state, setState] = useState(PullGesture.initial);
    const {indicator} = PullGesture.selectReactive(state);

    const controls = pipe(
        PullGesture.Controls.make({
            isRefreshing,
            onRefresh: useNonReactiveCallback(refreshHandler),
        }),
        useEqualsMemo,
        useUpdateSignal
    );

    useEffect(() => {
        const [content, spinner, scroller] = [contentRef.current, spinnerRef.current, scrollerRef.current];
        if (!content || !spinner || !scroller) {
            return;
        }

        const spinnerPadding = parseFloat(getComputedStyle(spinner).paddingBlockStart);

        let current = PullGesture.initial;

        const getContext = () => {
            return PullGesture.Context.make({
                ...controls.state,
                threshold: spinner.scrollHeight,
                resistance: 0.5,
                scroller,
                padding: spinnerPadding,
                returnDuration: 400,
            });
        };

        const dispatch = (action: PullGesture.Action) => {
            const next = PullGesture.reduce(current, {
                ...action,
                context: getContext(),
            });
            if (current.state !== next.state) {
                setState(next);
            }
            if (current !== next) {
                current = next;
                onUpdate();
            }
        };

        const onUpdate = () => {
            const {contentStyle, spinnerStyle, shouldGiveFeedback} = PullGesture.select(current, getContext());
            Object.assign(content.style, contentStyle);
            Object.assign(spinner.style, spinnerStyle);
            if (shouldGiveFeedback) {
                doFeedback();
            }
        };

        const {onTouchEnd, onTouchStart, onTouchMove, onTransitionEnd, onScroll} = bindActionCreators(
            {
                onTouchEnd: PullGesture.Action.end,
                onTouchStart: PullGesture.Action.start,
                onTouchMove: PullGesture.Action.move,
                onTransitionEnd: PullGesture.Action.returned,
                onScroll: PullGesture.Action.scrolled,
            },
            thunk => {
                thunk(dispatch, () => current, getContext);
            }
        );

        const [signal, abort] = createAbortControl();

        controls.addEventListener('update', onUpdate, {signal});
        signal.addEventListener('abort', onTouchEnd);

        content.addEventListener('transitionend', onTransitionEnd, {signal});
        content.addEventListener('touchstart', onTouchStart, {signal});
        content.addEventListener('touchmove', onTouchMove, {signal, passive: false});
        content.addEventListener('touchend', onTouchEnd, {signal});
        content.addEventListener('touchcancel', onTouchEnd, {signal});
        scroller.addEventListener('scroll', onScroll, {signal});

        return () => {
            abort();
        };
    }, []);

    return (
        <div ref={scrollerRef} style={{position: 'relative', overflowY: 'scroll', overscrollBehaviorY: 'none'}}>
            <div ref={contentRef}>{children}</div>
            <div
                ref={spinnerRef}
                style={{
                    visibility: indicator ? 'visible' : 'hidden',
                }}
                className='absolute left-0 right-0 top-0 grid place-items-center p-6'
            >
                <spinner.type {...spinner.props} spinning={isRefreshing} />
            </div>
        </div>
    );
}

namespace PullGesture {
    export class Controls extends Schema.Class<Controls>('PullGesture.Controls')({
        isRefreshing: Schema.Boolean,
        onRefresh: Schema.instanceOf(Function),
    }) {}

    export class Context extends Schema.Class<Context>('PullGesture.Context')({
        ...Controls.fields,
        resistance: Schema.Number,
        threshold: Schema.Number,
        padding: Schema.Number,
        returnDuration: Schema.Number,
        scroller: Schema.instanceOf(HTMLElement),
    }) {
        get maximum() {
            return this.threshold * 1.5;
        }

        get maximumWithResistance() {
            return this.maximum / this.resistance;
        }

        get startable() {
            return this.scroller.scrollTop < 1;
        }
    }

    export type Action = Readonly<
        | {
              kind: 'move';
              touchX: number;
              touchY: number;
          }
        | {
              kind: 'start';
              touchX: number;
              touchY: number;
          }
        | {
              kind: 'end';
          }
        | {
              kind: 'returned';
          }
        | {
              kind: 'scrolled';
              scrollTop: number;
          }
    >;

    type ActionWithContext = Action & {context: Context};

    export const Action = {
        start: (ev: TouchEvent) => (dispatch, getModel, getContext) => {
            const {startable} = getContext();
            const [touch, unwantedTouch] = ev.touches;
            if (getModel().state !== 'closed' || unwantedTouch || !startable) {
                return;
            }
            dispatch({kind: 'start', touchX: touch.clientX, touchY: touch.clientY});
        },
        move: (ev: TouchEvent) => (dispatch, getModel) => {
            const [touch, unwantedTouch] = ev.touches;
            if (getModel().state === 'closed' || unwantedTouch) {
                return;
            }
            dispatch({kind: 'move', touchX: touch.clientX, touchY: touch.clientY});
            if (getModel().state === 'pulling') {
                ev.preventDefault();
            }
        },
        scrolled: () => (dispatch, getModel, getContext) => {
            const {scroller} = getContext();
            if (getModel().state !== 'released') {
                return;
            }
            dispatch({kind: 'scrolled', scrollTop: scroller.scrollTop});
        },
        end: () => (dispatch, getModel, getContext) => {
            dispatch({kind: 'end'});
            const {shouldRefresh} = PullGesture.select(getModel(), getContext());
            if (shouldRefresh) {
                getContext().onRefresh();
            }
        },
        returned: () => dispatch => {
            dispatch({kind: 'returned'});
        },
    } satisfies {
        [key in string]: Thunk;
    };

    export type Thunk = (
        ...args: any[]
    ) => (dispatch: (action: Action) => void, getModel: () => PullGesture.Model, getContext: () => Context) => void;

    type View = {
        shouldRefresh: boolean;
        shouldGiveFeedback?: boolean;
        geometry: {translate: number; animated: boolean};
    };

    type ViewReactive = {
        indicator: boolean;
    };

    export type Model = Readonly<
        | {
              state: 'closed';
              animated?: boolean;
          }
        | {
              state: 'started';
              startX: number;
              startY: number;
          }
        | {
              state: 'pulling';
              startX: number;
              startY: number;
              pullY: number;
              feedback?: FeedbackState;
          }
        | {
              state: 'released';
              triggerable: boolean;
              scrollY: number;
          }
    >;

    enum FeedbackState {
        Disabled,
        Enabled,
        Given,
    }

    function reduceFeedback(model: Extract<Model, {feedback?: any}>, triggerable: boolean) {
        let {feedback = FeedbackState.Disabled} = model;
        if (triggerable) {
            if (feedback < FeedbackState.Given) {
                feedback = feedback + 1;
            }
        } else {
            feedback = FeedbackState.Disabled;
        }
        return feedback;
    }

    export const initial = identity<Model>({
        state: 'closed',
    });

    export function reduce(model: Model, action: ActionWithContext): typeof model {
        model = preReduce(model, action);
        if (hasKey(model, 'pullY')) {
            return {...model, pullY: translatePull(model.pullY, action.context)};
        } else {
            return model;
        }
    }

    function preReduce(model: Model, action: ActionWithContext): typeof model {
        const {isRefreshing, threshold} = action.context;
        if (model.state === 'closed') {
            if (action.kind === 'start') {
                return {state: 'started', startX: action.touchX, startY: action.touchY};
            }
        }
        if (model.state === 'started') {
            if (action.kind === 'move') {
                const dy = action.touchY - model.startY;
                const dx = action.touchX - model.startX;
                if (Math.abs(dx) > Math.abs(dy)) {
                    return {state: 'closed'};
                }
                if (dy < -1) {
                    return {state: 'closed'};
                }
                if (dy > +1) {
                    return {...model, state: 'pulling', pullY: dy};
                }
            }
            if (action.kind === 'end') {
                return {state: 'closed'};
            }
        }
        if (model.state === 'pulling') {
            const triggerable = model.pullY > threshold;
            if (action.kind === 'move') {
                return {
                    ...model,
                    pullY: action.touchY - model.startY,
                    feedback: reduceFeedback(model, triggerable),
                };
            }
            if (action.kind === 'end' && model.pullY > 1) {
                return {state: 'released', triggerable, scrollY: 0};
            }
            if (action.kind === 'end' && model.pullY < 1) {
                return {state: 'closed'};
            }
        }
        if (model.state === 'released') {
            if (action.kind === 'returned' && !isRefreshing) {
                return {state: 'closed'};
            }
            if (action.kind === 'scrolled') {
                if (model.scrollY > threshold / 2) {
                    return {state: 'closed', animated: true};
                } else {
                    return {...model, scrollY: action.scrollTop};
                }
            }
        }
        return model;
    }

    export function selectReactive(model: Model): ViewReactive {
        switch (model.state) {
            case 'pulling':
            case 'released':
                return {indicator: true};
            case 'closed':
            case 'started':
                return {indicator: false};
            default:
                return absurd(model);
        }
    }

    export function select(model: Model, context: Context) {
        const view = preSelect(model, context);
        const {translate, animated} = view.geometry;
        return {
            ...view,
            shouldGiveFeedback: view.shouldGiveFeedback ?? false,
            contentStyle: getContentStyle(translate, animated, context),
            spinnerStyle: getSpinnerStyle(translate, animated, context),
        };
    }

    function preSelect(model: Model, context: Context): View {
        const {isRefreshing, threshold} = context;
        switch (model.state) {
            case 'pulling': {
                const {pullY, feedback} = model;
                return {
                    shouldRefresh: false,
                    shouldGiveFeedback: feedback === FeedbackState.Enabled,
                    geometry: {translate: pullY, animated: false},
                };
            }
            case 'released': {
                if (isRefreshing) {
                    const translate = threshold - model.scrollY;
                    const animated = model.scrollY === 0;
                    return {
                        shouldRefresh: false,
                        geometry: {translate, animated},
                    };
                } else {
                    return {
                        shouldRefresh: !isRefreshing && model.triggerable,
                        geometry: {translate: 0, animated: true},
                    };
                }
            }
            case 'closed':
                return selectDefault(model);
            case 'started':
                return selectDefault();
            default:
                return absurd(model);
        }
    }

    function selectDefault({animated = false}: {animated?: boolean} = {}): View {
        return {shouldRefresh: false, geometry: {translate: 0, animated}};
    }

    function getContentStyle(translate: number, animated: boolean, context: Context) {
        const {returnDuration} = context;
        return {
            transform: `translateY(${translate}px)`,
            transition: animated === false ? '' : `transform ${returnDuration}ms ease`,
        };
    }

    function getSpinnerStyle(translate: number, animated: boolean, context: Context) {
        const {threshold, padding, returnDuration} = context;
        // TODO: добавить отступ от контента в 16 пикселей
        const position = Math.min(0, translate - threshold);

        // opacity animated during short stretch of movement from when spinner appears to when it stops
        // this stretch equals the padding of the spinner
        const opacity = percentBetween({
            lower: threshold - padding,
            upper: threshold,
        })(translate);
        // portion of the duration proportional to portion of threshold that padding takes up
        const opacityDuration = returnDuration / (threshold / padding);

        return {
            transform: `translateY(${position}px)`,
            transition:
                animated === false ? '' : `transform ${returnDuration}ms ease, opacity ${opacityDuration}ms ease`,
            opacity: opacity,
        };
    }

    /**
     * This function maps pull distance to a curve designed for a
     * special look and feel of the gesture.
     *
     * On desmos graph, `x` is the distance user has pulled,
     * and `y` is the distance the page will travel.
     *
     * @see https://www.desmos.com/calculator/w2tvakbqte
     *
     * If the link is no longer active, you can enter equations into desmos
     * to see the curve they should produce.
     */
    function clamp({minY: m, maxY: M}: {minY: number; maxY: number}) {
        const {min, max, exp} = Math;
        // implementation bellow should directly map to desmos graph
        return (x: number) => {
            const P = x / M;
            const L = min(x, M / 2);
            const U = M * (1 - 0.5 * exp(1 - 2 * P));

            const y = max(m, L, U);
            return y;
        };
    }

    /**
     * This function abstracts the total logic behind the feel of pull gesture.
     * It also ensures that maximum pull distance is correct after applying pull resistance.
     */
    function translatePull(pullY: number, context: Context) {
        return pipe(
            pullY,
            clamp({
                minY: 0,
                maxY: context.maximumWithResistance,
            }),
            multiply(context.resistance)
        );
    }

    function percentBetween({lower, upper}: {lower: number; upper: number}) {
        return (x: number) => {
            return Math.max(0, Math.min(1, (x - lower) / (upper - lower)));
        };
    }
}

function useUpdateSignal<T>(state: T) {
    const controller = useMemo(() => {
        const target = new EventTarget();
        let current = state;
        const signal = {
            get state() {
                return current;
            },
            addEventListener: identity<{
                (type: 'update', callback: EventListener, options?: AddEventListenerOptions): void;
            }>(target.addEventListener.bind(target)),
            removeEventListener: identity<{
                (type: 'update', callback: EventListener, options?: AddEventListenerOptions): void;
            }>(target.removeEventListener.bind(target)),
        };
        return {
            setState(update: T) {
                current = update;
                target.dispatchEvent(new Event('update'));
            },
            signal,
        };
    }, []);

    useEffect(() => {
        controller.setState(state);
    }, [state]);

    return controller.signal;
}

// function useThunksReducer<T, A, P extends any[]>(
//     reducer: React.Reducer<T, A>,
//     initializer: (...params: P) => T,
//     ...params: P
// ) {
//     const [state, setState] = useState(() => initializer(...params));
//     const dispatch = useCallback((thunk: (dispatch: (action: A) => void, getState: () => T) => void) => {
//         setState(current => {
//             thunk(
//                 (action: A) => {
//                     current = reducer(current, action);
//                 },
//                 () => current
//             );
//             return current;
//         });
//     }, []);
//     return [state, dispatch] as const;
// }

export default function bindActionCreators<M extends {[key: string]: (...args: any) => any}>(
    actionCreators: M,
    dispatch: (actions: ReturnType<M[keyof M]>) => void
) {
    type Out = {
        [K in keyof M]: (...params: Parameters<M[K]>) => void;
    };
    const boundActionCreators: Partial<Out> = {};
    for (const key in actionCreators) {
        const actionCreator = actionCreators[key];
        if (typeof actionCreator === 'function') {
            boundActionCreators[key] = function (this: any, ...args: any[]) {
                dispatch(actionCreator.apply(this, args));
            };
        }
    }
    return boundActionCreators as Out;
}

function hasKey<T extends {}, K extends T extends any ? keyof T : never>(
    thing: T,
    key: K
): thing is Extract<T, {[k in K]: any}> {
    return key in thing;
}
